if (!customElements.get("f-cart-goal")) {
  class FCartGoal extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        cartDrawer: ".f-cart-drawer__inner",
        defaultMessage: ".f-cartgoal__default-message",
      };

      this.confettiShow = false;
      this.goalDone = false;
      this.freeShippingCents = Number(this.dataset.freeShippingCents) || 7500;
      this.enableConfetti = this.dataset.enableConfetti === "true";
      this.supersedingCodes = new Set();
    }

    connectedCallback() {
      this.updateCartGoal(Number(this.dataset.cartTotal));
      window.FoxThemeEvents.subscribe(PUB_SUB_EVENTS.cartUpdate, (state) => {
        const kitCount = this.getKitCount(state);
        if (kitCount !== null) this.updateCartGoal(kitCount, state);
      });
      window.FoxThemeEvents.subscribe(PUB_SUB_EVENTS.cartUpdate, async () => {
        const newCart = await window.FoxThemeCartHelpers.getState();
        if (newCart) {
          this.updateCartGoal(this.getKitCount(newCart), newCart);
        }
      });
      this.confettiShow = this.goalDone;
    }

    getKitCount(cart) {
      if (!cart || (cart.item_count == null && !Array.isArray(cart.items))) return null;

      let kitCount = Number(cart.item_count) || 0;
      const giftWrapId = Number(this.dataset.giftWrapId) || 0;
      if (giftWrapId && Array.isArray(cart.items)) {
        const wrap = cart.items.find((item) => {
          return Number(item.id) === giftWrapId || Number(item.variant_id) === giftWrapId;
        });
        if (wrap) kitCount -= Number(wrap.quantity) || 0;
      }

      return Math.max(0, kitCount);
    }

    codeKey(value) {
      return String(value || "").trim().toLowerCase();
    }

    codeSupersedesBundle(cart) {
      if (!cart || !Array.isArray(cart.items)) {
        return this.dataset.codeSupersedes === "true";
      }

      const hasDiscountPayload =
        Array.isArray(cart.cart_level_discount_applications) ||
        cart.items.some((item) => Array.isArray(item.line_level_discount_allocations));
      const giftWrapId = Number(this.dataset.giftWrapId) || 0;
      let merchandise = Number(cart.original_total_price) || 0;
      const counted = new Set();
      const seenApplications = new Set();

      const consider = (application, amount) => {
        if (!application || application.type !== "discount_code") return;
        if (application.target_type === "shipping_line") return;
        const key = this.codeKey(application.title || application.code);
        if (!key || counted.has(key)) return;
        counted.add(key);
        seenApplications.add(key);

        const savings = Number(amount);
        const hasSavings = Number.isFinite(savings) && savings > 0;
        const hasPercent = application.value_type === "percentage" && application.value != null;
        const percent = hasPercent ? Number(application.value) : 0;
        if (!hasSavings && !hasPercent) return;

        const bundleSavings = Math.floor((Math.max(merchandise, 0) * 15) / 100);
        if ((hasPercent && percent > 15) || (hasSavings && savings > bundleSavings)) {
          this.supersedingCodes.add(key);
        } else {
          this.supersedingCodes.delete(key);
        }
      };

      cart.items.forEach((item) => {
        const isGiftWrap =
          giftWrapId &&
          (Number(item.id) === giftWrapId || Number(item.variant_id) === giftWrapId);
        if (isGiftWrap) merchandise -= Number(item.original_line_price) || 0;
      });

      (cart.cart_level_discount_applications || []).forEach((application) => {
        consider(application, application.total_allocated_amount);
      });
      cart.items.forEach((item) => {
        const isGiftWrap =
          giftWrapId &&
          (Number(item.id) === giftWrapId || Number(item.variant_id) === giftWrapId);
        if (isGiftWrap) return;
        (item.line_level_discount_allocations || []).forEach((allocation) => {
          consider(allocation.discount_application, allocation.amount);
        });
      });

      const presentCodes = Array.isArray(cart.discount_codes)
        ? cart.discount_codes
            .filter((discount) => discount && discount.applicable !== false && discount.code)
            .map((discount) => this.codeKey(discount.code))
        : null;

      if (presentCodes) {
        for (const key of this.supersedingCodes) {
          if (!presentCodes.includes(key)) this.supersedingCodes.delete(key);
        }
      }

      let supersedes = this.supersedingCodes.size > 0;
      if (!supersedes && this.dataset.codeSupersedes === "true") {
        if (!presentCodes) {
          supersedes = true;
        } else if (presentCodes.length > 0 && (!hasDiscountPayload || presentCodes.some((key) => !seenApplications.has(key)))) {
          supersedes = true;
        }
      }

      this.dataset.codeSupersedes = supersedes ? "true" : "false";
      return supersedes;
    }

    merchandiseCents(cart) {
      if (cart && cart.total_price != null) return Number(cart.total_price) || 0;
      return Number(this.dataset.cartSubtotal) || 0;
    }

    spendMessage(centsLeft) {
      const template =
        this.dataset.msgAmount || "Spend __AMOUNT__ more for free shipping!";
      const formatted =
        typeof formatMoney === "function"
          ? formatMoney(centsLeft, window.FoxThemeSettings?.money_format || "${{amount}}")
          : `$${(centsLeft / 100).toFixed(2)}`;
      return template.replace("__AMOUNT__", formatted);
    }

    updateCartGoal(kitCount, cart) {
      kitCount = Number(kitCount) || 0;
      const subtotal = this.merchandiseCents(cart);
      this.dataset.cartSubtotal = subtotal;

      if (kitCount > 0) {
        this.classList.remove("hidden");
      } else {
        this.classList.add("hidden");
      }

      const threshold = this.freeShippingCents;
      this.goalDone = subtotal >= threshold;
      this.percent = threshold > 0 ? (subtotal * 100) / threshold : 0;
      if (this.percent >= 100) this.percent = 100;

      const supersedes = this.codeSupersedesBundle(cart);
      const shippingMessage =
        this.dataset.msgShipping || "Congratulations! Your order qualifies for free shipping!";
      const doneMessage =
        this.dataset.msgDone || "Congratulations! Your order qualifies for free shipping!";
      const bundleDone = !supersedes && kitCount >= 3;
      const successMessage = bundleDone ? doneMessage : shippingMessage;
      const amountLeft = Math.max(0, threshold - subtotal);
      const messageEl = this.querySelector(this.selectors.defaultMessage);
      const successEl = this.querySelector(".f-cartgoal__success-message");

      if (messageEl) {
        if (this.goalDone) {
          messageEl.textContent = successMessage;
        } else if (!supersedes && kitCount <= 1) {
          messageEl.textContent =
            this.dataset.msgOne || "Add a second set to save 10%!";
        } else if (!supersedes && kitCount === 2) {
          messageEl.textContent =
            this.dataset.msgTwo || "Add a third set to save 15%!";
        } else {
          messageEl.textContent = this.spendMessage(amountLeft);
        }
      }

      if (successEl) {
        successEl.textContent = successMessage;
      }

      this.style.setProperty("--progress", `${this.percent}%`);
      this.dataset.total = kitCount;

      if (this.goalDone) {
        this.classList.add("f-cartgoal--done", "shakeY");
      } else {
        this.classList.remove("f-cartgoal--done", "shakeY");
      }

      if (this.goalDone && this.enableConfetti) {
        this.showConfetti();
      } else {
        this.confettiShow = false;
      }
    }

    showConfetti() {
      const cartDrawer = this.closest(this.selectors.cartDrawer);
      const cartTemplate = this.closest(".cart-template");

      if (!this.confettiShow && (cartDrawer || cartTemplate)) {
        setTimeout(() => {
          if (cartDrawer) {
            new window.FoxTheme.Confetti(cartDrawer);
          }
          if (cartTemplate) {
            new window.FoxTheme.Confetti(document.body);
          }
          this.confettiShow = true;
        }, 800);
      }
    }
  }

  customElements.define("f-cart-goal", FCartGoal);
}
